import { currentMonthKey } from "./dateWindow";
import { isSubRow, subMagnitude } from "./subconsultants";

// PROJECT CURRENCY (firm model, 2026-09)
// Fees are always ENTERED and STORED in NZD — NZD is the single source of
// truth and nothing about it ever moves. A project may additionally record a
// client-facing currency; the foreign figure beneath an NZD amount is purely a
// display conversion.
//
// TWO RATES, deliberately:
//   · LOCKED — `project.fxRate`, captured when it was set (by hand, or from a
//     live rate on that day). Applies to every month already gone AND to every
//     invoiced figure, so nothing already reported can move.
//   · LIVE — today's ECB reference rate from `GET /api/fx/latest`. Applies to
//     forecast months (this month onwards) only.
// A blended total is therefore past×locked + forecast×live, and the "as at"
// date of the live day is always shown alongside it.
export const CURRENCIES = ["NZD", "AUD", "USD", "GBP", "EUR", "SGD", "JPY", "CNY", "AED"];

export const SYMBOLS = {
  NZD: "$", AUD: "A$", USD: "US$", GBP: "£", EUR: "€",
  SGD: "S$", JPY: "¥", CNY: "CN¥", AED: "AED ",
};

// The ECB publishes reference rates for 30 currencies — AED is NOT one of
// them, so an AED project keeps a hand-entered rate.
export const ECB_UNSUPPORTED = ["AED"];
export const supportsLiveRate = (code) =>
  !!code && code !== "NZD" && !ECB_UNSUPPORTED.includes(code);

export const fxRound = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

export const hasAltCurrency = (p) =>
  !!p && (p.currency || "NZD") !== "NZD" && Number(p.fxRate) > 0;

export const lockedRateOf = (p) => Number(p?.fxRate) || 0;
export const liveRateOf = (p, fx) =>
  fx?.rates && p?.currency ? Number(fx.rates[p.currency]) || 0 : 0;

// Which rate applies to a given month key: past stays locked, forecast follows
// the live rate when one is available.
export function rateForMonth(project, month, fx) {
  const locked = lockedRateOf(project);
  const live = liveRateOf(project, fx);
  if (!month) return live || locked;
  return month < currentMonthKey() ? locked : live || locked;
}

export function fmtAltValue(v, code, { compact = false } = {}) {
  const n = Number(v) || 0;
  if (!n) return null;
  const sym = SYMBOLS[code] || `${code} `;
  const abs = Math.abs(n);
  const body = compact && abs >= 1000
    ? `${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`
    : abs.toLocaleString("en-NZ", { maximumFractionDigits: 0 });
  return `${n < 0 ? "−" : ""}${sym}${body} ${code}`;
}

// LOCKED-rate conversion — invoiced and historical figures, which must never
// move when the market does.
export const altAmount = (nzd, project) =>
  hasAltCurrency(project) ? (Number(nzd) || 0) * lockedRateOf(project) : null;

export function fmtAlt(nzd, project, opts) {
  const v = altAmount(nzd, project);
  if (v === null) return null;
  return fmtAltValue(v, project.currency, opts);
}

// Blended project total: every stored month converted at the rate that applies
// to it, then summed. `weighted` applies each scope's probability.
export function altProjectTotal(project, fx, { weighted = false } = {}) {
  if (!hasAltCurrency(project)) return null;
  const locked = lockedRateOf(project);
  const live = liveRateOf(project, fx) || locked;
  const cur = currentMonthKey();
  let out = 0;
  for (const s of project.scopes || []) {
    const w = weighted ? (s._originalProbability ?? s.probability ?? 1) : 1;
    for (const [mo, amt] of Object.entries(s.revenue || {})) {
      out += (Number(amt) || 0) * w * (mo < cur ? locked : live);
    }
  }
  return out;
}

// Same rule applied to the project form's Total / Less subs / Net trio across
// the visible month window. A sub-consultant fee with no monthly spread is
// forward-looking, so it converts at the live rate.
export function altTrio(scopes, months, project, fx) {
  if (!hasAltCurrency(project)) return null;
  const locked = lockedRateOf(project);
  const live = liveRateOf(project, fx) || locked;
  const cur = currentMonthKey();
  let gross = 0;
  let subs = 0;
  for (const s of scopes || []) {
    let spread = 0;
    let converted = 0;
    for (const mo of months || []) {
      const v = Number(s?.revenue?.[mo]) || 0;
      spread += v;
      converted += v * (mo < cur ? locked : live);
    }
    if (isSubRow(s)) subs += spread ? Math.abs(converted) : subMagnitude(s) * live;
    else gross += converted;
  }
  return { gross, subs, net: gross - subs, locked, live };
}

// "31 Aug 2026" — the ECB day the cached rate belongs to. Always displayed, so
// a weekend / public-holiday rate can never look stuck.
export function fxAsAt(fx) {
  if (!fx?.date) return null;
  const d = new Date(`${fx.date}T00:00:00`);
  return isNaN(d.getTime())
    ? fx.date
    : d.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });
}

// One-line provenance for the live rate.
export function fxLiveLabel(project, fx) {
  const code = project?.currency;
  if (!code || code === "NZD") return null;
  const live = liveRateOf(project, fx);
  if (!live) {
    return supportsLiveRate(code)
      ? "Live rate not available right now — enter it by hand"
      : `The ECB does not publish ${code} — enter the rate by hand`;
  }
  return `1 NZD = ${fxRound(live)} ${code} · ECB as at ${fxAsAt(fx)}${
    fx?.stale ? " · last known day, feed unreachable" : ""
  }`;
}

// Which rate(s) a converted figure used — shown wherever a blended figure is.
export function fxRuleCaption(project, fx) {
  if (!hasAltCurrency(project)) return null;
  const locked = fxRound(lockedRateOf(project));
  const live = liveRateOf(project, fx);
  if (!live) {
    return `Converted at the locked rate ${locked} ${project.currency} per NZD — no live ECB rate for ${project.currency}.`;
  }
  return `Forecast months at the live ECB rate ${fxRound(live)} (as at ${fxAsAt(fx)}) · earlier months and every invoiced figure at the locked ${locked}.`;
}
